import React from 'react';
import { Box } from '@mui/material';
import { cardSx } from '../constants';
import { errorMessage } from '../lib/errorMessage';
import { dateRangeLabel } from '../lib/format';
import useProfitIqTabs from '../hooks/useProfitIqTabs';
import { useDateRange } from '../hooks/useSelection';
import { useScreenLoading } from './ScreenLoading';
import { EmptyState, ErrorView, RefreshingOverlay } from './States';

/**
 * True when the business has never imported or connected anything.
 *
 * `itemsTotal` is the length of the whole menu, not of the page of it the
 * dashboard carries — an established business with no sales in the selected
 * window still has a menu, and must not be offered the import screen.
 */
export function isEmptyBusiness(data) {
  return data.channels.length === 0 && data.itemsTotal === 0;
}

/**
 * The gate every dashboard tab opens with.
 *
 * Renders `children(data)` only once there is a dashboard worth drawing.
 * Before that it resolves, in order: a first load, a failed load, a business
 * with nothing imported yet, and a date window that contains no sales. Those
 * last two are distinct on purpose — one is fixed by importing, the other by
 * changing the range — so each gets the action that fixes it.
 *
 * A refetch behind an edit is not a blocking state: the rows stay on screen
 * under `RefreshingOverlay` so nothing collapses under the user mid-edit. Only
 * a query with no data at all earns the loader, and since the cache is keyed by
 * business and period, a switch of either lands there rather than showing the
 * previous selection's figures.
 *
 * That first load is reported to the section rather than drawn here, so the
 * loader covers the date bar and the tab strip too and no control is reachable
 * while the figures are on their way. `id` is what the view reports under.
 *
 * Pass `allowNoData` on tabs that still have something to show for an empty
 * window (the cost editors, which are about next month as much as this one).
 */
export default function ScreenState({
  id,
  query,
  loadingLabel,
  emptyIcon,
  emptyTitle = 'No data yet',
  emptyMessage = 'Import your sales from Clover, Square, Orders.co or another POS to see your profit health, channels and menu margins.',
  allowNoData = false,
  children,
}) {
  const { goToImport } = useProfitIqTabs();
  const { range, customLabel, month, setRange } = useDateRange();
  const { data, isFetching, error } = query;

  const dateLabel = dateRangeLabel(range, month, customLabel);

  const showLoader = isFetching && !data;
  useScreenLoading(id, showLoader ? loadingLabel : null);

  if (showLoader) return null;
  if (error && !data) return <ErrorView message={errorMessage(error)} />;
  if (!data) return <ErrorView message="No data available" />;

  let body;
  if (isEmptyBusiness(data)) {
    body = (
      <Box sx={{ ...cardSx }}>
        <EmptyState
          icon={emptyIcon}
          title={emptyTitle}
          message={emptyMessage}
          ctaLabel="Import your data"
          onCta={goToImport}
        />
      </Box>
    );
  } else if (data.hasData === false && !allowNoData) {
    body = (
      <Box sx={{ ...cardSx }}>
        <EmptyState
          title={`No data for ${dateLabel}`}
          message="There are no orders for this period. Try a different date range or import more data."
          ctaLabel="View this month"
          onCta={() => setRange('thismonth')}
        />
      </Box>
    );
  } else {
    body = children(data);
  }

  return (
    <Box sx={{ position: 'relative', pb: 4 }}>
      <RefreshingOverlay visible={isFetching && !!data} />
      {body}
    </Box>
  );
}
